(() => {
  const DRAWER_SELECTOR = '[data-vl-dealer-drawer]';
  const WIDGET_SELECTOR = '[data-vl-dealer-drawer-widget]';
  const STOREPOINT_EMBED_SRC = 'https://widget.storepoint.co/embed.js';
  const STOREPOINT_LOAD_TIMEOUT = 15000;
  const STOREPOINT_POLL_INTERVAL = 50;
  const MAX_TARGET_ATTEMPTS = 40;
  const TARGET_RETRY_DELAY = 100;
  const SHARED_LOADER_KEY = '__vlStorepointLoaderPromise';

  let requestId = 0;
  let storepointLifecycle = Promise.resolve();
  let drawerWidget = null;

  function ensureStorepointQueue() {
    window.Storepoint = window.Storepoint || {
      _q: [],
      on(eventName, callback) {
        this._q.push([eventName, callback]);
      },
    };
  }

  function findStorepointScript() {
    return Array.from(document.scripts).find((script) =>
      script.src.startsWith(STOREPOINT_EMBED_SRC),
    );
  }

  function loadStorepoint() {
    if (typeof window.StorepointWidget === 'function') {
      return Promise.resolve(window.StorepointWidget);
    }

    if (window[SHARED_LOADER_KEY]) return window[SHARED_LOADER_KEY];

    ensureStorepointQueue();

    const loader = new Promise((resolve, reject) => {
      let script = findStorepointScript();
      let pollTimer = null;
      let timeoutTimer = null;
      let settled = false;

      if (script?.dataset.vlStorepointFailed === 'true') {
        script.remove();
        script = null;
      }

      const cleanup = () => {
        window.clearInterval(pollTimer);
        window.clearTimeout(timeoutTimer);
        script?.removeEventListener('load', handleLoad);
        script?.removeEventListener('error', handleError);
      };

      const settle = (constructor, error) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (constructor) resolve(constructor);
        else reject(error);
      };

      const resolveWhenReady = () => {
        if (typeof window.StorepointWidget !== 'function') return false;
        if (script) script.dataset.vlStorepointLoaded = 'true';
        settle(window.StorepointWidget);
        return true;
      };

      function handleLoad() {
        resolveWhenReady();
      }

      function handleError() {
        if (script) script.dataset.vlStorepointFailed = 'true';
        settle(null, new Error('Unable to load Storepoint.'));
      }

      const isNewScript = !script;
      if (!script) {
        script = document.createElement('script');
        script.src = STOREPOINT_EMBED_SRC;
        script.async = true;
        script.dataset.vlDealerDrawerStorepoint = 'true';
      }

      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
      pollTimer = window.setInterval(resolveWhenReady, STOREPOINT_POLL_INTERVAL);
      timeoutTimer = window.setTimeout(() => {
        settle(null, new Error('Timed out while loading Storepoint.'));
      }, STOREPOINT_LOAD_TIMEOUT);

      if (isNewScript) document.head.appendChild(script);
      resolveWhenReady();
    });

    window[SHARED_LOADER_KEY] = loader.catch((error) => {
      delete window[SHARED_LOADER_KEY];
      throw error;
    });
    return window[SHARED_LOADER_KEY];
  }

  function getWidgetTarget(drawer, attempt = 0) {
    const target = drawer.querySelector(WIDGET_SELECTOR);
    if (target || attempt >= MAX_TARGET_ATTEMPTS) return Promise.resolve(target);

    return new Promise((resolve) => {
      window.setTimeout(() => {
        resolve(getWidgetTarget(drawer, attempt + 1));
      }, TARGET_RETRY_DELAY);
    });
  }

  function queueStorepointTask(task) {
    storepointLifecycle = storepointLifecycle.then(task, task);
    return storepointLifecycle;
  }

  function getDealerMaps() {
    return Array.from(document.querySelectorAll('vl-dealer-map'));
  }

  async function destroyCurrentStorepoint() {
    if (typeof window.Storepoint?.destroy !== 'function') return;
    await Promise.resolve(window.Storepoint.destroy());
  }

  async function destroyDrawerWidget() {
    const widget = drawerWidget;
    drawerWidget = null;

    if (typeof widget?.destroy === 'function') {
      await Promise.resolve(widget.destroy());
      return;
    }

    await destroyCurrentStorepoint();
  }

  async function suspendDealerMaps() {
    await Promise.all(getDealerMaps().map((dealerMap) => (
      dealerMap.suspendStorepointWidget?.()
    )));
  }

  function resumeDealerMaps() {
    getDealerMaps().forEach((dealerMap) => {
      dealerMap.resumeStorepointWidget?.();
    });
  }

  async function initializeDrawer(drawer, storepointTag, currentRequestId) {
    try {
      const target = await getWidgetTarget(drawer);

      if (
        !target
        || !target.isConnected
        || currentRequestId !== requestId
        || !drawer.classList.contains('is-opened')
      ) return;

      await suspendDealerMaps();
      await destroyCurrentStorepoint();

      if (
        !target.isConnected
        || currentRequestId !== requestId
        || !drawer.classList.contains('is-opened')
      ) {
        resumeDealerMaps();
        return;
      }

      const StorepointWidgetConstructor = await loadStorepoint();

      if (
        !target.isConnected
        || currentRequestId !== requestId
        || !drawer.classList.contains('is-opened')
      ) {
        resumeDealerMaps();
        return;
      }

      target.replaceChildren();
      const options = { mapOptions: { disabled: true } };
      if (storepointTag) options.tags = [storepointTag];

      drawerWidget = new StorepointWidgetConstructor(
        drawer.dataset.storepointAccountId,
        `#${target.id}`,
        options,
      );
      drawerWidget.load?.();
    } catch (error) {
      try {
        await destroyDrawerWidget();
      } catch (destroyError) {
        console.error('[VL Dealer Drawer]', destroyError);
      }
      resumeDealerMaps();
      console.error('[VL Dealer Drawer]', error);
    }
  }

  async function releaseDrawerStorepoint(drawer) {
    requestId += 1;

    try {
      await destroyDrawerWidget();
    } catch (error) {
      console.error('[VL Dealer Drawer]', error);
    }

    drawer.querySelector(WIDGET_SELECTOR)?.replaceChildren();
    resumeDealerMaps();
  }

  function observeDrawer(drawer) {
    let wasOpened = drawer.classList.contains('is-opened');

    const observer = new MutationObserver(() => {
      const isOpened = drawer.classList.contains('is-opened');
      if (wasOpened && !isOpened) {
        queueStorepointTask(() => releaseDrawerStorepoint(drawer));
      }
      wasOpened = isOpened;
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function handleDocumentClick(event) {
    const button = event.target.closest('[data-sidebar-button][data-sidebar-id]');
    if (!button) return;

    const drawer = document.querySelector(`${DRAWER_SELECTOR}#${button.dataset.sidebarId}`);
    if (!drawer || drawer.contains(button)) return;

    requestId += 1;
    const currentRequestId = requestId;
    const storepointTag = button.dataset.storepointTag?.trim() || '';

    window.setTimeout(() => {
      queueStorepointTask(() => initializeDrawer(drawer, storepointTag, currentRequestId));
    });
  }

  document.querySelectorAll(DRAWER_SELECTOR).forEach(observeDrawer);
  document.addEventListener('click', handleDocumentClick, true);
})();
